import React, { Fragment, useEffect } from 'react';
import { Nullable, SelectOption } from 'global';
import { formatCurrency } from 'utils';
import { Info, Security } from '@mui/icons-material';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { Box, CircularProgress, Divider, Stack, Typography } from '@mui/material';
import { RenderWithFallback } from 'components/shared/Conditional/RenderWithFallback';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';
import { AutocompleteField, CurrencyInputField, InterestInputField } from 'components/form';
import { EmptyProducts } from './EmptuProducts';

export interface RevenueCardFormValues {
  proposal: Nullable<SelectOption>;
  products: RevenueProductData[];
}

interface RevenueProductData {
  id: string;
  name: string;
  dateSold: string;
  cost: number;
  markupFlat: number;
  markupPercent: number;
}

export interface AftermarketProposalData {
  totals: { markupFlat: number };
  products: RevenueProductData[];
}

export interface AftermarketRevenueCardProps {
  isOptionsLoading: boolean;
  proposalListOptions: SelectOption[];
  onCreateProductQuote: VoidFunction;
  isProposalRevenueDataLoading: boolean;
  proposalRevenueData: AftermarketProposalData;
  getRevenueCardInitialFormValues: () => Promise<RevenueCardFormValues>;
  handleGetProposal: (proposalId: string) => Promise<Nullable<AftermarketProposalData>>;
  onFormStateChange?: (formValues: RevenueCardFormValues) => void;
}

export const RevenueCard = ({
  isOptionsLoading,
  handleGetProposal,
  proposalRevenueData,
  onCreateProductQuote,
  proposalListOptions = [],
  isProposalRevenueDataLoading,
  getRevenueCardInitialFormValues,
  onFormStateChange,
}: AftermarketRevenueCardProps) => {
  const { control, setValue, getValues } = useForm<RevenueCardFormValues>({
    defaultValues: getRevenueCardInitialFormValues,
  });

  const { fields } = useFieldArray({ control, name: 'products' });
  const proposalValue = useWatch({ control, name: 'proposal' });
  const productsValue = useWatch({ control, name: 'products' });

  // Expose form state to parent component
  useEffect(() => {
    if (onFormStateChange) {
      const currentValues = getValues();
      onFormStateChange(currentValues);
    }
  }, [proposalValue, productsValue, onFormStateChange, getValues]);

  return (
    <Box className="revenueCard">
      <RenderWithCondition
        condition={isOptionsLoading || !!proposalListOptions.length}
        fallBack={
          <Stack alignItems="center" justifyContent="center" gap="0.5rem">
            <Info color="disabled" sx={{ fontSize: '3rem' }} />
            <Typography>There is no Accepted proposals for this VO</Typography>
          </Stack>
        }
      >
        <Box className="revenueCardHeader">
          <Box className="revenueCardHeaderIconWithTitle">
            <Box className="revenueCardHeaderIcon">
              <Security />
            </Box>

            <Typography variant="subtitle1" component="span">
              Protection Products
            </Typography>
          </Box>
        </Box>

        <Box className="revenueAftermarketContent">
          <AutocompleteField
            name="proposal"
            control={control}
            isLoading={isOptionsLoading}
            options={proposalListOptions}
            disabled={!proposalListOptions.length}
            extraAction={async nextValue => {
              if (nextValue?.value) {
                const response = await handleGetProposal(nextValue.value);
                setValue('products', response?.products ?? []);
              }
            }}
            textFieldProps={{
              size: 'small',
              label: 'Proposal',
              variant: 'outlined',
              placeholder: 'Search proposal...',
            }}
          />

          <RenderWithCondition condition={!!proposalValue}>
            <RenderWithFallback
              isLoading={isProposalRevenueDataLoading}
              fallBack={
                <Box className="revenueAftermarketLoading">
                  <CircularProgress />
                  <Typography>Loading products...</Typography>
                </Box>
              }
            >
              <RenderWithCondition
                condition={!!proposalRevenueData.products.length}
                fallBack={<EmptyProducts onCreateProductQuote={onCreateProductQuote} />}
              >
                {fields.map(({ id, name, dateSold }, index) => (
                  <Fragment key={id}>
                    <Box className="revenueAftermarketProductsListItem">
                      <Box className="productInfoSection">
                        <Typography variant="body2" color="textDisabled">
                          Date Sold: {dateSold}
                        </Typography>
                        <Typography variant="body1">{name}</Typography>
                      </Box>

                      <Box className="productPriceSection">
                        <CurrencyInputField
                          control={control}
                          label="Markup ($)"
                          name={`products.${index}.markupFlat`}
                          textFieldProps={{ variant: 'outlined', disabled: true }}
                        />
                        <InterestInputField
                          control={control}
                          label="Markup (%)"
                          name={`products.${index}.markupPercent`}
                          textFieldProps={{ variant: 'outlined', disabled: true }}
                        />
                      </Box>
                    </Box>

                    <Divider />
                  </Fragment>
                ))}
              </RenderWithCondition>
            </RenderWithFallback>

            <Box className="revenueAftermarketTotal">
              <Typography color="textDisabled">Total Markup</Typography>

              <RenderWithFallback
                isLoading={isProposalRevenueDataLoading}
                fallBack={<Typography>Calculating...</Typography>}
              >
                <Typography>{formatCurrency(proposalRevenueData.totals.markupFlat, true)}</Typography>
              </RenderWithFallback>
            </Box>
          </RenderWithCondition>
        </Box>
      </RenderWithCondition>
    </Box>
  );
};
